/**
 * Office.js bridge layer. Everything that reads from or writes to the Word
 * document goes through here, so the rest of the React tree doesn't need to
 * know about the callback-based Office API.
 */
/** Resolve once Office.js is ready inside Word. Call this before mounting React. */
export function initOffice() {
    return new Promise((resolve) => {
        const timeout = setTimeout(resolve, 5000);
        Office.onReady(() => {
            clearTimeout(timeout);
            resolve();
        });
    });
}
/**
 * Convert the current Word document to PDF and return its bytes.
 * Word Desktop converts client-side; Word Online does the conversion on
 * Microsoft's servers and streams the result back in slices.
 */
export async function getDocumentAsPdf() {
    return new Promise((resolve, reject) => {
        Office.context.document.getFileAsync(Office.FileType.Pdf, { sliceSize: 4 * 1024 * 1024 }, async (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                reject(new Error(`Failed to get PDF: ${result.error.message}`));
                return;
            }
            const file = result.value;
            try {
                const chunks = [];
                for (let i = 0; i < file.sliceCount; i += 1) {
                    const slice = await getSlice(file, i);
                    chunks.push(new Uint8Array(slice.data));
                }
                const total = chunks.reduce((s, c) => s + c.byteLength, 0);
                const out = new Uint8Array(total);
                let off = 0;
                for (const c of chunks) {
                    out.set(c, off);
                    off += c.byteLength;
                }
                resolve(out.buffer);
            }
            catch (e) {
                reject(e instanceof Error ? e : new Error(String(e)));
            }
            finally {
                file.closeAsync();
            }
        });
    });
}
function getSlice(file, index) {
    return new Promise((resolve, reject) => {
        file.getSliceAsync(index, (r) => {
            if (r.status === Office.AsyncResultStatus.Failed)
                reject(new Error(`Slice ${index} failed: ${r.error.message}`));
            else
                resolve(r.value);
        });
    });
}
/** Best-effort document title from `Office.context.document.url`. */
export function getDocumentName() {
    try {
        const url = Office.context?.document?.url || '';
        const filename = url.split('/').pop() || url.split('\\').pop() || 'Untitled';
        return filename.replace(/\.(docx?|DOCX?)$/, '') || 'Untitled document';
    }
    catch {
        return 'Untitled document';
    }
}
/** Insert a base64 PNG signature image at the current selection. */
export async function insertSignatureImage(base64Png, widthPx = 200, heightPx = 80) {
    // Strip any data: URL prefix; Word.insertInlinePictureFromBase64 wants raw.
    const clean = base64Png.startsWith('data:') ? base64Png.split(',')[1] ?? '' : base64Png;
    await Word.run(async (ctx) => {
        const selection = ctx.document.getSelection();
        const image = selection.insertInlinePictureFromBase64(clean, Word.InsertLocation.replace);
        image.width = widthPx;
        image.height = heightPx;
        await ctx.sync();
    });
}
const TAG_TYPES = ['SIGN', 'DATE', 'INITIALS', 'TEXT', 'NAME', 'EMAIL'];
/** Insert a coloured placeholder tag at the current cursor position. */
export async function insertPlaceholderTag(tagType, signerIndex = 1) {
    const tag = `{{${tagType}_${signerIndex}}}`;
    await Word.run(async (ctx) => {
        const selection = ctx.document.getSelection();
        const range = selection.insertText(tag, Word.InsertLocation.replace);
        range.font.color = '#1f74f2'; // brand blue
        range.font.bold = true;
        range.font.size = 11;
        range.font.highlightColor = '#DBEAFE';
        await ctx.sync();
    });
    return tag;
}
/** Walk the document body and return every `{{TYPE_N}}` tag we find. */
export async function findPlaceholderTags() {
    const found = [];
    await Word.run(async (ctx) => {
        const body = ctx.document.body;
        for (const t of TAG_TYPES) {
            const results = body.search(`{{${t}_`, { matchCase: true, matchWholeWord: false });
            results.load('text');
            await ctx.sync();
            for (const item of results.items) {
                const match = item.text.match(/\{\{(SIGN|DATE|INITIALS|TEXT|NAME|EMAIL)_(\d+)\}\}/);
                if (match) {
                    found.push({
                        tag: match[0],
                        type: match[1],
                        signerIndex: parseInt(match[2], 10),
                    });
                }
            }
        }
    });
    return found;
}
/** Strip every Bleinks placeholder tag from the document. Used after send. */
export async function removePlaceholderTags() {
    await Word.run(async (ctx) => {
        const body = ctx.document.body;
        for (const t of TAG_TYPES) {
            const results = body.search(`{{${t}_`, { matchCase: true, matchWholeWord: false });
            results.load('text');
            await ctx.sync();
            for (const item of results.items)
                item.delete();
            await ctx.sync();
        }
    });
}
/** Open a URL in the user's default browser via the Office dialog API. */
export function openUrl(url) {
    // displayDialogAsync with displayInIframe: false opens in a system browser
    // on Desktop; on Web it opens a popup window. Either way the user lands at
    // the URL outside Word.
    Office.context.ui.displayDialogAsync(url, { height: 80, width: 80, displayInIframe: false }, () => {
        /* no-op: we don't need to listen to the new browser window. */
    });
}
